/**
 * @file Dashboard.js
 * @description View principal que exibe as estatísticas e classificações.
 */
import { useState } from "react";
import { Users, Shield, BarChart2, FileDown, Star, ChevronDown } from "lucide-react";
import TabelaClassificacaoGeral from "../components/TabelaGeral";
import { getEscudoUrl } from "../api/ligaApi";

const StatCard = ({ icon, title, value, accentColor }) => (
  <div className="bg-gray-800 border border-gray-700/50 rounded-xl p-4 sm:p-5 flex items-center gap-4 relative overflow-hidden shadow-md">
    <div className={`absolute left-0 inset-y-0 w-1 rounded-l-xl ${accentColor}`} />
    <div className="p-2.5 sm:p-3 rounded-xl bg-white/5 flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-xs sm:text-sm text-gray-400 font-medium">{title}</p>
      <p className="text-2xl sm:text-3xl font-black text-white mt-0.5 tabular-nums">{value}</p>
    </div>
  </div>
);

const Dashboard = ({ ligaData, handleGerarPDF, handleGerarPDFGeral, isAdmin }) => {
  const { equipes, rodadas } = ligaData;
  const [selectedRodada, setSelectedRodada] = useState(isAdmin ? 1 : 1);

  const rodadasDisponiveis = isAdmin
    ? [...Array(38).keys()].map((i) => i + 1)
    : [...Array(19).keys()].map((i) => i + 1);

  const rodadasComPontos = rodadas.filter((r) => r.pontos != null);
  const totalRodadasFinalizadas = [
    ...new Set(rodadasComPontos.map((r) => r.numero)),
  ].length;

  const getPontosPorTimeNaRodada = (timeId, rodadaNum) => {
    const ponto = rodadas.find(
      (r) =>
        r.numero === rodadaNum &&
        r.time_id &&
        r.time_id.toString() === timeId.toString(),
    );
    return ponto ? ponto.pontos : 0;
  };

  const getPontosPorEquipeNaRodada = (equipe, rodadaNum) =>
    equipe.times.reduce(
      (total, time) => total + getPontosPorTimeNaRodada(time.id, rodadaNum),
      0,
    );

  const equipesPorRodada = [...equipes]
    .map((equipe) => ({
      ...equipe,
      pontosNaRodada: getPontosPorEquipeNaRodada(equipe, selectedRodada),
    }))
    .sort((a, b) => b.pontosNaRodada - a.pontosNaRodada);

  const rodadaTemDados = equipesPorRodada.some((e) => e.pontosNaRodada > 0);

  return (
    <div className="space-y-6">
      {/* Cards de estatísticas */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <StatCard
          icon={<Users size={20} className="text-blue-400" />}
          title="Equipes na Disputa"
          value={equipes.length}
          accentColor="bg-blue-500"
        />
        <StatCard
          icon={<Shield size={20} className="text-green-400" />}
          title="Total de Times"
          value={equipes.reduce((acc, equipe) => acc + equipe.times.length, 0)}
          accentColor="bg-green-500"
        />
        <StatCard
          icon={<BarChart2 size={20} className="text-yellow-400" />}
          title="Rodadas Finalizadas"
          value={totalRodadasFinalizadas}
          accentColor="bg-yellow-500"
        />
      </div>

      {/* Classificação Geral */}
      <div className="bg-gray-800 border border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-700/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-white">Classificação Geral</h3>
            <p className="text-xs text-gray-400 mt-0.5">Pontuação acumulada de todas as rodadas</p>
          </div>
          {isAdmin && (
            <button
              onClick={handleGerarPDFGeral}
              className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
            >
              <FileDown size={15} />
              Exportar PDF
            </button>
          )}
        </div>
        <div className="p-4 sm:p-5">
          <TabelaClassificacaoGeral equipes={equipes} rodadasData={rodadas} />
        </div>
      </div>

      {/* Detalhes por Rodada */}
      <div className="bg-gray-800 border border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-gray-700/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-base sm:text-lg font-bold text-white">Detalhes da Rodada</h3>
              <p className="text-xs text-gray-400 mt-0.5">Pontuação por equipe em cada rodada</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Select customizado */}
              <div className="relative flex-1 sm:flex-none">
                <select
                  value={selectedRodada}
                  onChange={(e) => setSelectedRodada(Number(e.target.value))}
                  className="w-full sm:w-auto appearance-none bg-gray-700 border border-gray-600 text-white py-2 pl-3 pr-8 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
                >
                  {rodadasDisponiveis.map((rodadaNum) => (
                    <option key={rodadaNum} value={rodadaNum}>
                      Rodada {rodadaNum}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
              {isAdmin && (
                <button
                  onClick={() => handleGerarPDF(selectedRodada)}
                  className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg transition-colors text-sm flex-shrink-0"
                >
                  <FileDown size={15} />
                  <span className="hidden sm:inline">PDF</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {/* Empty state quando não há dados na rodada */}
          {!rodadaTemDados ? (
            <div className="text-center py-10">
              <BarChart2 size={36} className="text-gray-700 mx-auto mb-3" />
              <p className="text-gray-400 font-medium text-sm">Sem dados para esta rodada</p>
              <p className="text-gray-600 text-xs mt-1">
                As pontuações ainda não foram sincronizadas.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {equipesPorRodada.map((equipe, index) => (
                <div
                  key={equipe.id}
                  className="bg-gray-900/60 border border-gray-700/50 rounded-xl overflow-hidden"
                >
                  {/* Cabeçalho da equipe */}
                  <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-gray-700/30">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className="text-sm font-bold text-gray-500 w-4 text-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <img
                        src={getEscudoUrl(equipe.logo_url) || "/logo-equipe-padrao.png"}
                        alt=""
                        className="w-9 h-9 rounded-full object-contain flex-shrink-0"
                      />
                      <div className="flex items-center gap-1.5 min-w-0">
                        <h4 className="font-bold text-white text-sm sm:text-base truncate">
                          {equipe.nome}
                        </h4>
                        {equipe.titulos > 0 && (
                          <div
                            className="relative flex items-center justify-center w-5 h-5 flex-shrink-0"
                            title={`${equipe.titulos} Títulos`}
                          >
                            <Star size={20} className="text-yellow-400 fill-yellow-400" />
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-black text-gray-900">
                              {equipe.titulos}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 ml-2">
                      <span className="text-xl sm:text-2xl font-black text-white tabular-nums">
                        {equipe.pontosNaRodada.toFixed(2)}
                      </span>
                      <span className="text-gray-500 text-xs ml-1">pts</span>
                    </div>
                  </div>

                  {/* Lista de times */}
                  <div className="divide-y divide-gray-700/30">
                    {equipe.times
                      .sort((a, b) => a.nome_time.localeCompare(b.nome_time))
                      .map((time) => (
                        <div
                          key={time.id}
                          className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5"
                        >
                          <img
                            src={getEscudoUrl(time.escudo_url)}
                            alt={`Escudo do ${time.nome_time}`}
                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-white text-sm truncate flex items-center gap-1.5">
                              <span className="truncate">{time.nome_time}</span>
                              {time.is_captain && (
                                <span className="text-yellow-400 font-bold text-xs flex-shrink-0">
                                  (C)
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {time.nome_cartoleiro}
                            </p>
                          </div>
                          <span className="font-bold text-teal-400 text-sm tabular-nums flex-shrink-0">
                            {getPontosPorTimeNaRodada(time.id, selectedRodada).toFixed(2)}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
